'use client'

import { useState } from 'react'
import { Check, Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import { MorphExpandIcon } from '@/components/ui/morph-expand-icon'
import { Checkbox } from '@/components/ui/checkbox'
import { dropdownTriggerInteractionClassName } from '@/components/ui/control-styles'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export type FilterOption = { value: string; label: string; count?: number }

export type FilterDef = {
  /** Stable key used in the emitted filter state. */
  id: string
  /** Placeholder shown on the trigger when nothing is selected. */
  label: string
  options: FilterOption[]
  /** Allow selecting several options (checkbox list). Defaults to single-select. */
  multiple?: boolean
  /** Show a search box inside the dropdown. Defaults to `multiple`. */
  searchable?: boolean
}

/** Selected option values keyed by filter id. Empty/absent = no filter for that key. */
export type FilterValues = Record<string, string[]>

export type SearchFilterState = { search: string; filters: FilterValues }

function FilterSelect({
  def,
  value,
  onChange,
}: {
  def: FilterDef
  value: string[]
  onChange: (next: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const multiple = def.multiple ?? false
  const searchable = def.searchable ?? multiple
  const count = value.length

  const toggle = (v: string) => {
    if (multiple) {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
    } else {
      onChange(value[0] === v ? [] : [v])
      setOpen(false)
    }
  }

  const triggerLabel = multiple
    ? count
      ? `${def.label} (${count})`
      : def.label
    : count
      ? (def.options.find((o) => o.value === value[0])?.label ?? def.label)
      : def.label

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            'border-input bg-card flex h-11 w-full items-center justify-between gap-2 rounded-lg border px-4 text-sm outline-none',
            dropdownTriggerInteractionClassName,
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-2',
            count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <MorphExpandIcon expanded={open} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-56 p-0">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${def.label}`} />}
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {def.options.map((opt) => {
                const selected = value.includes(opt.value)
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => toggle(opt.value)}
                    className={cn(
                      'cursor-pointer',
                      selected &&
                        'bg-sidebar-accent text-sidebar-primary data-[selected=true]:bg-sidebar-accent',
                    )}
                  >
                    {multiple && <Checkbox checked={selected} className="pointer-events-none" />}
                    <span>{opt.label}</span>
                    {opt.count != null && (
                      <span className="text-muted-foreground text-sm">({opt.count})</span>
                    )}
                    {!multiple && selected && <Check className="text-primary ml-auto size-4" />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
          {multiple && count > 0 && (
            <div className="border-t p-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-muted-foreground hover:bg-interaction-neutral hover:text-foreground w-full rounded-sm px-2 py-1.5 text-sm font-medium transition-colors"
              >
                Clear ({count})
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Search + filter bar. Works uncontrolled (manages its own state) or controlled
 * (pass `value`) — the controlled mode lets a parent keep the state in the URL.
 * Emits the full `{ search, filters }` on every change via `onChange`. Results
 * are rendered by the consumer (see `DataList`).
 */
export function SearchFilterBar({
  filters,
  searchPlaceholder = 'Search...',
  value,
  onChange,
  className,
}: {
  filters: FilterDef[]
  searchPlaceholder?: string
  value?: SearchFilterState
  onChange?: (state: SearchFilterState) => void
  className?: string
}) {
  const [internal, setInternal] = useState<SearchFilterState>({ search: '', filters: {} })
  const state = value ?? internal

  const emit = (next: SearchFilterState) => {
    if (value === undefined) setInternal(next)
    onChange?.(next)
  }

  const setFilter = (id: string, next: string[]) => {
    const nextFilters = { ...state.filters }
    if (next.length) nextFilters[id] = next
    else delete nextFilters[id]
    emit({ search: state.search, filters: nextFilters })
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-4 size-4 -translate-y-1/2" />
        <Input
          type="search"
          value={state.search}
          onChange={(e) => emit({ search: e.target.value, filters: state.filters })}
          placeholder={searchPlaceholder}
          className="h-12 rounded-lg pl-11 text-base"
        />
      </div>
      <div className="flex flex-wrap gap-3">
        {filters.map((def) => (
          <div key={def.id} className="min-w-48 flex-1">
            <FilterSelect
              def={def}
              value={state.filters[def.id] ?? []}
              onChange={(next) => setFilter(def.id, next)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
